import {
  ChangeEvent,
  useCallback,
  useMemo,
  useState,
  type FunctionComponent,
} from 'react';
import { useSelector } from 'react-redux';

import { pageTypeSelector } from '@/core/page/page.selectors';
import { Gtm } from '@/domains/core/tracking/utils/Gtm';
import type {
  Item,
  MultiSelectType,
} from '@/domains/productDiscovery/Listings/components/Filters/components/MultiSelect/interfaces';
import { MultiSelect } from '@/domains/productDiscovery/Listings/components/Filters/components/MultiSelect/MultiSelect';
import { NB_FACETS_VISIBLE } from '@/domains/productDiscovery/Listings/constants';
import { useFacet } from '@/domains/productDiscovery/Listings/facets/hooks/useFacet';
import { useCategoryFamilyId } from '@/domains/productDiscovery/Listings/pages/Category/hooks/useCategoryFamilyId';
import { useCategoryId } from '@/domains/productDiscovery/Listings/pages/Category/hooks/useCategoryId';
import { useCategoryLevel } from '@/domains/productDiscovery/Listings/pages/Category/hooks/useCategoryLevel';
import { MergedValue } from '@/domains/productDiscovery/microservices/msfProductListing/interfaces/microservice';
import { FilterSearchBar } from '@/productDiscovery/Listings/components/Filters/components/FilterSearchBar/FilterSearchBar';
import { interactionFilterSearchBar } from '@/productDiscovery/Listings/components/Filters/tracking/generateFiltersTracking';

import { FilterBlock } from '../FilterBlock/FilterBlock';
import { generateItemsFromFacet } from './generateItemFromFacet/generateItemsFromFacet';

interface Props {
  className?: string;
  title: string;
  facetName: string;
  position?: number;
  filterCount?: number;
  type?: MultiSelectType;
  mergedValues?: MergedValue[];
  searchBar?: boolean;
  selection?: string[];
}

export const MultiSelectFilter: FunctionComponent<Props> = ({
  className,
  title,
  facetName,
  position,
  filterCount,
  type = 'checkbox',
  mergedValues,
  searchBar = false,
  selection,
}) => {
  const categoryId = useCategoryId();
  const categoryLevel = useCategoryLevel();
  const categoryFamilyId = useCategoryFamilyId();
  const pageType = useSelector(pageTypeSelector);

  const { facet, refineFacetValue } = useFacet<typeof type>(facetName);
  const [expanded, setExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const items = useMemo(
    () => generateItemsFromFacet(facet?.values, facetName, mergedValues),
    [facet?.values, facetName, mergedValues],
  );

  const handleChange = useCallback(
    async (_: unknown, selectedFacet: Item) => {
      if (selectedFacet) {
        refineFacetValue(selectedFacet.value);

        const { generateFiltersTracking } = await import(
          '../../tracking/generateFiltersTracking'
        );

        Gtm.push(
          generateFiltersTracking({
            e_label: title,
            filter_id: facetName,
            filter_name: title,
            filter_value: selectedFacet.value,
            filter_position: position,
            filter_count: filterCount,
            category_id: categoryId,
            category_level: categoryLevel,
            family_id: categoryFamilyId,
            is_highlighted: !!(
              !searchValue && selection?.includes(selectedFacet.value)
            ),
          }),
        );
      }
    },
    [
      categoryFamilyId,
      categoryId,
      categoryLevel,
      facetName,
      filterCount,
      position,
      refineFacetValue,
      searchValue,
      selection,
      title,
    ],
  );

  if (!facet?.values.length) return null;

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) =>
    setSearchValue(event.target.value);

  // NOTE Use onBlur to improve INP when the search input is clicked
  const handleSearchBlur = () =>
    Gtm.push(
      interactionFilterSearchBar({
        page_type: pageType,
        filter_name: facetName,
      }),
    );

  const toggleExpansion = () => setExpanded(!expanded);

  const displaySelection = selection && selection.length > 0;
  const selectedItems = items.filter((item) => selection?.includes(item.value));
  const itemsWithoutSelection = displaySelection
    ? items
        .filter((item) => !selection.includes(item.value))
        // NOTE Prefer this solution instead of localCompare due to performance.
        //      Return all the filters sorted by label in alphabetical order (only for "selection" case).
        .sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0))
    : items;

  const itemsWithSearchValue = items.filter(({ value }) =>
    value.toLowerCase().includes(searchValue.toLowerCase()),
  );

  return (
    <FilterBlock
      className={className}
      id={facetName}
      title={title}
      expandable={!searchValue && facet.values.length > NB_FACETS_VISIBLE}
      expanded={expanded}
      onExpandedChange={toggleExpansion}
    >
      {searchBar && (
        <FilterSearchBar
          onChange={handleSearchChange}
          onBlur={handleSearchBlur}
          onResetValue={() => setSearchValue('')}
          value={searchValue}
        />
      )}

      {!searchValue && displaySelection && (
        <MultiSelect
          items={selectedItems}
          onChange={handleChange}
          type={type}
        />
      )}

      {!searchValue && (
        <MultiSelect
          items={
            !expanded
              ? itemsWithoutSelection.slice(
                  0,
                  NB_FACETS_VISIBLE - selectedItems.length,
                )
              : itemsWithoutSelection
          }
          topSeparator={displaySelection}
          onChange={handleChange}
          type={type}
        />
      )}

      {searchValue && (
        <MultiSelect
          items={itemsWithSearchValue}
          onChange={handleChange}
          type={type}
        />
      )}
    </FilterBlock>
  );
};

MultiSelectFilter.displayName = 'MultiSelectFilter';
